export interface TreatmentPlanProgressApiResource {
  done: number;
  skipped: number;
  refused: number;
}

export interface TreatmentPlanProgress {
  done: number;
  skipped: number;
  refused: number;
}

export interface TreatmentPlanItemApiResource {
  id: string;
  treatment_id: string;
  treatment_name: string;
  instructions: string | null;
  sort_order: number;
  progress: TreatmentPlanProgressApiResource;
}

export interface TreatmentPlanItem {
  id: string;
  treatmentId: string;
  treatmentName: string;
  instructions: string | null;
  sortOrder: number;
  progress: TreatmentPlanProgress;
}

export interface ExerciseComplianceCountsApiResource {
  total: number;
  last_completed_on: string | null;
}

export interface ExerciseComplianceCounts {
  total: number;
  lastCompletedOn: string | null;
}

export interface TreatmentPlanExerciseApiResource {
  id: string;
  name: string;
  instructions: string | null;
  frequency: string | null;
  sort_order: number;
  compliance: ExerciseComplianceCountsApiResource;
}

export interface TreatmentPlanExercise {
  id: string;
  name: string;
  instructions: string | null;
  frequency: string | null;
  sortOrder: number;
  compliance: ExerciseComplianceCounts;
}

export interface TreatmentPlanApiResource {
  id: string;
  patient_id: string;
  version: number;
  status: string;
  therapist_notes: string | null;
  client_visible_at: string | null;
  items: TreatmentPlanItemApiResource[];
  exercises: TreatmentPlanExerciseApiResource[];
  created_at: string;
  updated_at: string;
}

export interface TreatmentPlan {
  id: string;
  patientId: string;
  version: number;
  status: string;
  therapistNotes: string | null;
  clientVisibleAt: string | null;
  items: TreatmentPlanItem[];
  exercises: TreatmentPlanExercise[];
  createdAt: string;
  updatedAt: string;
}

export interface TreatmentPlanVersionSummaryApiResource {
  id: string;
  version: number;
  status: string;
  created_at: string;
}

export interface TreatmentPlanVersionSummary {
  id: string;
  version: number;
  status: string;
  createdAt: string;
}

export interface ClientTreatmentPlanItemApiResource {
  treatment_name: string;
  instructions: string | null;
  progress: TreatmentPlanProgressApiResource;
}

export interface ClientTreatmentPlanItem {
  treatmentName: string;
  instructions: string | null;
  progress: TreatmentPlanProgress;
}

export interface ExerciseCompletionEntryApiResource {
  completed_on: string;
  note: string | null;
}

export interface ExerciseCompletionEntry {
  completedOn: string;
  note: string | null;
}

export interface ExerciseCompletionRollupApiResource {
  done_today: boolean;
  total: number;
  last_completed_on: string | null;
  recent: ExerciseCompletionEntryApiResource[];
}

export interface ExerciseCompletionRollup {
  doneToday: boolean;
  total: number;
  lastCompletedOn: string | null;
  recent: ExerciseCompletionEntry[];
}

export interface ClientTreatmentPlanExerciseApiResource {
  id: string;
  name: string;
  instructions: string | null;
  frequency: string | null;
  completion: ExerciseCompletionRollupApiResource;
}

export interface ClientTreatmentPlanExercise {
  id: string;
  name: string;
  instructions: string | null;
  frequency: string | null;
  completion: ExerciseCompletionRollup;
}

export interface ExerciseComplianceApiResource {
  id: string;
  name: string;
  frequency: string | null;
  total_completions: number;
  last_completed_on: string | null;
  recent: ExerciseCompletionEntryApiResource[];
  video_status: string;
}

export interface ExerciseCompliance {
  id: string;
  name: string;
  frequency: string | null;
  totalCompletions: number;
  lastCompletedOn: string | null;
  recent: ExerciseCompletionEntry[];
  videoStatus: string;
}

export interface ClientTreatmentPlanApiResource {
  patient_id: string;
  patient_name: string;
  items: ClientTreatmentPlanItemApiResource[];
  exercises: ClientTreatmentPlanExerciseApiResource[];
  shared_at: string;
}

export interface ClientTreatmentPlan {
  patientId: string;
  patientName: string;
  items: ClientTreatmentPlanItem[];
  exercises: ClientTreatmentPlanExercise[];
  sharedAt: string;
}

export interface TreatmentPlanItemInput {
  treatmentId: string;
  instructions: string | null;
  sortOrder: number;
}

export interface TreatmentPlanExerciseInput {
  name: string;
  instructions: string | null;
  frequency: string | null;
  sortOrder: number;
}

export interface TreatmentPlanWritePayload {
  therapistNotes: string | null;
  items: TreatmentPlanItemInput[];
  exercises: TreatmentPlanExerciseInput[];
}

function mapTreatmentPlanProgress(
  resource: TreatmentPlanProgressApiResource
): TreatmentPlanProgress {
  return {
    done: resource.done,
    skipped: resource.skipped,
    refused: resource.refused,
  };
}

function mapTreatmentPlanItem(resource: TreatmentPlanItemApiResource): TreatmentPlanItem {
  return {
    id: resource.id,
    treatmentId: resource.treatment_id,
    treatmentName: resource.treatment_name,
    instructions: resource.instructions,
    sortOrder: resource.sort_order,
    progress: mapTreatmentPlanProgress(resource.progress),
  };
}

function mapExerciseComplianceCounts(
  resource: ExerciseComplianceCountsApiResource
): ExerciseComplianceCounts {
  return {
    total: resource.total,
    lastCompletedOn: resource.last_completed_on,
  };
}

function mapExerciseCompletionEntry(
  resource: ExerciseCompletionEntryApiResource
): ExerciseCompletionEntry {
  return {
    completedOn: resource.completed_on,
    note: resource.note,
  };
}

function mapExerciseCompletionRollup(
  resource: ExerciseCompletionRollupApiResource
): ExerciseCompletionRollup {
  return {
    doneToday: resource.done_today,
    total: resource.total,
    lastCompletedOn: resource.last_completed_on,
    recent: resource.recent.map(mapExerciseCompletionEntry),
  };
}

function mapTreatmentPlanExercise(
  resource: TreatmentPlanExerciseApiResource
): TreatmentPlanExercise {
  return {
    id: resource.id,
    name: resource.name,
    instructions: resource.instructions,
    frequency: resource.frequency,
    sortOrder: resource.sort_order,
    compliance: mapExerciseComplianceCounts(resource.compliance),
  };
}

export function mapApiResourceToTreatmentPlan(resource: TreatmentPlanApiResource): TreatmentPlan {
  return {
    id: resource.id,
    patientId: resource.patient_id,
    version: resource.version,
    status: resource.status,
    therapistNotes: resource.therapist_notes,
    clientVisibleAt: resource.client_visible_at,
    items: resource.items.map(mapTreatmentPlanItem),
    exercises: resource.exercises.map(mapTreatmentPlanExercise),
    createdAt: resource.created_at,
    updatedAt: resource.updated_at,
  };
}

export function mapApiResourceToTreatmentPlanVersionSummary(
  resource: TreatmentPlanVersionSummaryApiResource
): TreatmentPlanVersionSummary {
  return {
    id: resource.id,
    version: resource.version,
    status: resource.status,
    createdAt: resource.created_at,
  };
}

function mapClientTreatmentPlanItem(
  resource: ClientTreatmentPlanItemApiResource
): ClientTreatmentPlanItem {
  return {
    treatmentName: resource.treatment_name,
    instructions: resource.instructions,
    progress: mapTreatmentPlanProgress(resource.progress),
  };
}

export function mapApiResourceToClientTreatmentPlanExercise(
  resource: ClientTreatmentPlanExerciseApiResource
): ClientTreatmentPlanExercise {
  return {
    id: resource.id,
    name: resource.name,
    instructions: resource.instructions,
    frequency: resource.frequency,
    completion: mapExerciseCompletionRollup(resource.completion),
  };
}

export function mapApiResourceToExerciseCompliance(
  resource: ExerciseComplianceApiResource
): ExerciseCompliance {
  return {
    id: resource.id,
    name: resource.name,
    frequency: resource.frequency,
    totalCompletions: resource.total_completions,
    lastCompletedOn: resource.last_completed_on,
    recent: resource.recent.map(mapExerciseCompletionEntry),
    videoStatus: resource.video_status,
  };
}

export function mapApiResourceToClientTreatmentPlan(
  resource: ClientTreatmentPlanApiResource
): ClientTreatmentPlan {
  return {
    patientId: resource.patient_id,
    patientName: resource.patient_name,
    items: resource.items.map(mapClientTreatmentPlanItem),
    exercises: resource.exercises.map(mapApiResourceToClientTreatmentPlanExercise),
    sharedAt: resource.shared_at,
  };
}

export function mapTreatmentPlanWritePayload(
  payload: TreatmentPlanWritePayload
): Record<string, unknown> {
  return {
    therapist_notes: payload.therapistNotes,
    items: payload.items.map(function toApiItem(item) {
      return {
        treatment_id: item.treatmentId,
        instructions: item.instructions,
        sort_order: item.sortOrder,
      };
    }),
    exercises: payload.exercises.map(function toApiExercise(exercise) {
      return {
        name: exercise.name,
        instructions: exercise.instructions,
        frequency: exercise.frequency,
        sort_order: exercise.sortOrder,
      };
    }),
  };
}

export interface ExerciseCompletionCreatePayload {
  completedOn?: string;
  note?: string;
}

export function mapExerciseCompletionCreatePayload(
  payload: ExerciseCompletionCreatePayload
): Record<string, unknown> {
  const mapped: Record<string, unknown> = {};
  if (payload.completedOn !== undefined) {
    mapped.completed_on = payload.completedOn;
  }
  if (payload.note !== undefined) {
    mapped.note = payload.note;
  }
  return mapped;
}
